import { PlanType } from '../../utils/plans';

export enum Frequency {
  MONTH = 'MONTH',
}

export interface Plan {
  title: string;
  price: { [key in Frequency]: string };
  recommended: boolean;
  features: string[];
  charges: string;
}

export type AllPlansIncludingLegacy =
  | PlanType.INDIVIDUAL_STARTER
  | PlanType.INDIVIDUAL_PREMIUM
  | PlanType.INDIVIDUAL_PRO
  | PlanType.BUSINESS_STARTER
  | PlanType.BUSINESS_PREMIUM
  | PlanType.BUSINESS_PRO
  | PlanType.LEGACY_PREMIUM
  | PlanType.GROW
  | PlanType.PREMIUM
  | PlanType.PRO
  | PlanType.PAY_AS_YOU_GO;
export const NEW_PLANS = [
  PlanType.PAY_AS_YOU_GO,
  PlanType.GROW,
  PlanType.PREMIUM,
  PlanType.PRO,
];

/**
 * A legacy plan is any paid plan that isn't one of NEW_PLANS. Derived rather
 * than listed so that adding a plan to NEW_PLANS is all that's needed to keep
 * new plans out of the retirement messaging.
 *
 * FREE is excluded: there is no paid subscription to migrate off, so the
 * retirement copy doesn't apply.
 */
export const isLegacyPlan = (planType?: PlanType): planType is PlanType =>
  planType !== undefined &&
  planType !== PlanType.FREE &&
  !(NEW_PLANS as PlanType[]).includes(planType);

/**
 * Retired plans that keep full access to the app for now.
 *
 * Being retired and being locked out are separate questions. These merchants
 * are still on a legacy plan and still see the retirement messaging — they are
 * simply not forced onto the plan page yet. Emptying this list completes the
 * block; it is not a permanent tier.
 */
export const BLOCK_EXEMPT_PLANS = [PlanType.BUSINESS_PRO];

/**
 * Whether a plan is locked out of the app: legacy, minus the exemptions above.
 *
 * Kept apart from `isLegacyPlan` so the retirement copy and the pre-switch
 * warning stay driven by "is this plan retired?", which is a different question
 * from "should this merchant be redirected?".
 */
export const isBlockedPlan = (planType?: PlanType): planType is PlanType =>
  isLegacyPlan(planType) &&
  !(BLOCK_EXEMPT_PLANS as PlanType[]).includes(planType);

const plans: Record<AllPlansIncludingLegacy, Plan> = {
  // These only exist to show the plan card for legacy cards.
  // {{{
  INDIVIDUAL_STARTER: {
    title: 'Legacy Individual Starter',
    price: {
      MONTH: '$9.99/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: false,
    features: [
      '20 NFTs and COAs/mo',
      '1 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Online support',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
      '',
    ],
  },
  INDIVIDUAL_PREMIUM: {
    title: 'Legacy Individual Premium',
    price: {
      MONTH: '$19.99/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: true,
    features: [
      '120 NFTs & certificates /mo',
      '2 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Add your branding',
      '20-minute onboarding call',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  INDIVIDUAL_PRO: {
    title: 'Legacy Individual Pro',
    price: {
      MONTH: '$49.99/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: false,
    features: [
      '1000 NFTs and COAs/mo',
      '5 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Add your branding',
      'Dedicated account manager',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  BUSINESS_STARTER: {
    title: 'Legacy Business Starter',
    price: {
      MONTH: '$29.99/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: false,
    features: [
      '5 represented creators',
      '100 NFTs and COAs/mo',
      '5 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Online support',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  BUSINESS_PREMIUM: {
    title: 'Legacy Business Premium',
    price: {
      MONTH: '$59.99/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: true,
    features: [
      '10 represented creators',
      '500 NFTs and COAs/mo',
      '10 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Add your branding',
      '20-minute onboarding call',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  BUSINESS_PRO: {
    title: 'Legacy Business Pro',
    price: {
      MONTH: '$199.99/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: false,
    features: [
      '50 represented creators',
      '5000 NFTs and COAs/mo',
      '50 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Add your branding',
      'Dedicated account manager',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  LEGACY_PREMIUM: {
    title: 'Legacy Premium',
    price: {
      MONTH: '$10/month',
    },
    charges: 'Fixed fee per COA and transaction fee per NFT',
    recommended: false,
    features: [
      '1 verified Creator',
      '1,000 COAs/mo',
      '5 custom contracts',
      'Ethereum & Polygon',
      'Unlimited tokengates',
      'Supports multiple languages',
      'Custom branding',
      'Online support',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  // }}}

  GROW: {
    title: 'Growth',
    price: {
      MONTH: '$29.99/month',
    },
    charges:
      'For growing professionals with lower fixed prices per COA and transaction fee per NFT. Exceeding quota charged at $1 minimum per transaction.',
    recommended: false,
    features: [
      '1 verified Creator',
      '50 COAs/mo',
      'Shopify integration',
      'Editions module',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  PREMIUM: {
    title: 'Premium',
    price: {
      MONTH: '$49.99/month',
    },
    charges:
      'For small businesses with more than one creator. Includes custom branding for certificates. Exceeding quota charged at $1 minimum per transaction.',
    recommended: true,
    features: [
      '2 represented creators',
      '100 COAs/mo',
      'Shopify integration',
      'Editions module',
      'Custom branding',
      '5% off QR and NFC packs',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  PRO: {
    title: 'Pro',
    price: {
      MONTH: '$99.99/month',
    },
    charges:
      'For power users and mid tier businesses. Ideal for merchants and galleries who want to certify multiple creators. Exceeding quota charged at $1 minimum per transaction.',
    recommended: false,
    features: [
      '10 represented creators',
      '1,000 COAs/mo',
      'Shopify integration',
      'Editions module',
      'Custom branding',
      '$0.25 per COA',
      '5% fee /$0.25 minimum per NFT sale',
    ],
  },
  PAY_AS_YOU_GO: {
    title: 'Pay as you sell',
    // Not a monthly price — this plan is a single up-front charge with no
    // recurring fee. The value is rendered as a plain string (see Pricing.tsx),
    // so the MONTH key is just where the display price lives.
    price: {
      MONTH: '$9.99 one-time',
    },
    charges:
      'For occasional users. Pay only upon sale. One time activation fee. Usage fees per COA & NFT. Shopify will ask you to approve the one-time fee first, then the usage charges.',
    recommended: false,
    features: [
      '1 verified Creator',
      '50 custom contracts',
      '0.5% fee, $1.00 min per COA',
      '7.5% fee, $1.00 min per NFT sale',
    ],
  },
};

export default plans;
